/**
 * The precomputed TrendPulse and CTS datasets, fetched once and kept.
 *
 * <h2>What is stored</h2>
 * Three centuries of daily values, 1801-2100 — 109,573 rows per file. Uncompressed that is ~4 MB
 * each; served gzipped it is 0.28-0.71 MB over the wire, and the bucket sets
 * `Cache-Control: immutable` because a file named for its own date range cannot change.
 *
 * <h2>Two caches, for two different failures</h2>
 * The in-memory map stops a second study in the same session re-parsing 4 MB of JSON. IndexedDB
 * stops a page reload re-downloading it. Neither is the other: the HTTP cache would usually cover
 * the download, but "usually" is doing a lot of work there — a hard reload, a cache eviction under
 * pressure, or a browser with storage restrictions all bypass it, and 4 MB is enough for a customer
 * to notice.
 *
 * <h2>Failure is a fallback, not an error</h2>
 * Every entry point resolves to null rather than throwing when a dataset cannot be had. The caller
 * then runs the live study, which is what it did before these files existed. A precomputed dataset
 * is an optimisation; a study that stops working because a bucket is unreachable would be a
 * regression dressed as one.
 */

import appConfig from '../config/appConfig';

/* eslint-disable no-undef */
/**
 * Where the datasets live.
 *
 * <p>Env var first so a region or a staging bucket can override at build time, then the value
 * baked into appConfig, then the US bucket. The trailing slash is normalised rather than assumed:
 * a base URL supplied without one silently produces `...precomputedtrendpulse_...json`.
 */
const CONFIGURED_BASE = process.env.REACT_APP_PRECOMPUTED_BASE_URL
  || appConfig.precomputedBaseUrl
  || 'https://storage.googleapis.com/astrocharts-precomputed/';
/* eslint-enable no-undef */

export const PRECOMPUTED_BASE_URL = CONFIGURED_BASE.endsWith('/')
  ? CONFIGURED_BASE : `${CONFIGURED_BASE}/`;

/** The span every published dataset covers. */
export const DATASET_FROM_YEAR = 1801;
export const DATASET_TO_YEAR = 2100;

/** The CTS orbs that have a published file. Anything else has to run live. */
export const PRECOMPUTED_CTS_ORBS = [1.0, 1.5, 2.0, 3.0];

/** `1.5` -> `1_5`, matching how the CLI names its output. */
export function orbTag(orb) {
  return Number(orb).toFixed(1).replace('.', '_');
}

export function trendPulseFileName() {
  return `trendpulse_${DATASET_FROM_YEAR}_${DATASET_TO_YEAR}.json`;
}

export function ctsFileName(orb) {
  return `cts_reversals_orb_${orbTag(orb)}_${DATASET_FROM_YEAR}_${DATASET_TO_YEAR}.json`;
}

/**
 * The span the 30-minute scan covers, which is NOT the span of the reversals file.
 *
 * The reversals files run 1801-2100 and are a merge of two sampling regimes: 30-minute inside
 * these years, one reading a day outside them. The detail file exists only for the scanned span,
 * so its name carries that span and not the dataset's.
 */
export const CTS_DETAIL_FROM_YEAR = 1990;
export const CTS_DETAIL_TO_YEAR = 2026;

export function ctsDetailFileName(orb) {
  return `cts_detail_orb_${orbTag(orb)}_${CTS_DETAIL_FROM_YEAR}_${CTS_DETAIL_TO_YEAR}.json`;
}

/**
 * Whether an orb can be served from a file.
 *
 * <p>Compared on the TAG rather than on the number, so 1.5 and 1.50 are the same question and
 * floating-point equality never decides whether a download is attempted.
 */
export function hasPrecomputedOrb(orb) {
  return PRECOMPUTED_CTS_ORBS.some(o => orbTag(o) === orbTag(orb));
}

// ─────────────────────────────────────────────────────────────────────── caches

/** Parsed datasets, by file name. Survives for the life of the tab. */
const memory = new Map();

/** In-flight requests, by file name — see loadDataset. */
const inFlight = new Map();

const DB_NAME = 'astrocharts-precomputed';
const DB_VERSION = 1;
const STORE = 'datasets';

/**
 * The IndexedDB handle, or null where there is no IndexedDB.
 *
 * <p>jsdom has none, and neither does a browser in some private modes. Both must degrade to the
 * memory cache rather than failing, so every call site treats null as "no persistent cache" and
 * carries on.
 */
function openDb() {
  return new Promise((resolve) => {
    let idb = null;
    try {
      idb = typeof indexedDB !== 'undefined' ? indexedDB : null;
    } catch (_) {
      idb = null;                       // access itself throws under some storage policies
    }
    if (!idb) { resolve(null); return; }

    let request;
    try {
      request = idb.open(DB_NAME, DB_VERSION);
    } catch (_) {
      resolve(null);
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    // A blocked upgrade never fires either handler. Without this the study would wait forever on
    // a cache lookup, which is a worse failure than not having a cache.
    request.onblocked = () => resolve(null);
  });
}

function idbGet(key) {
  return openDb().then(db => new Promise((resolve) => {
    if (!db) { resolve(null); return; }
    try {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    } catch (_) {
      resolve(null);
    }
  }));
}

function idbPut(key, value) {
  return openDb().then(db => new Promise((resolve) => {
    if (!db) { resolve(false); return; }
    try {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(value, key);
      tx.oncomplete = () => resolve(true);
      // Quota is the expected failure on a 4 MB write, and it is not worth reporting: the memory
      // cache still holds the data for this session and the next visit re-downloads.
      tx.onerror = () => resolve(false);
      tx.onabort = () => resolve(false);
    } catch (_) {
      resolve(false);
    }
  }));
}

/** Forgets everything cached. Exported for tests and for a "reload data" control. */
export async function clearPrecomputedCache() {
  memory.clear();
  inFlight.clear();
  const db = await openDb();
  if (!db) return;
  try {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).clear();
  } catch (_) { /* nothing to clear */ }
}

// ─────────────────────────────────────────────────────────────────────── loading

/**
 * One dataset: memory, then IndexedDB, then the network.
 *
 * <p>`inFlight` is what makes "once per session" true under concurrency. Two studies opened in the
 * same second would otherwise each miss the cache, each start a 4 MB download and each parse it —
 * the second one arriving only to overwrite an identical result. Sharing the promise means the
 * second caller waits on the first request rather than making a second.
 *
 * @returns {Promise<Array|null>} the rows, or null when the dataset could not be had
 */
export async function loadDataset(fileName, { signal } = {}) {
  if (memory.has(fileName)) return memory.get(fileName);
  if (inFlight.has(fileName)) return inFlight.get(fileName);

  const request = (async () => {
    const cached = await idbGet(fileName);
    if (Array.isArray(cached) && cached.length > 0) {
      memory.set(fileName, cached);
      return cached;
    }

    const url = `${PRECOMPUTED_BASE_URL}${fileName}`;
    try {
      const res = await fetch(url, { signal, mode: 'cors', credentials: 'omit' });
      if (!res.ok) {
        console.warn(`[precomputed] ${fileName}: HTTP ${res.status} — falling back to the live study`);
        return null;
      }
      const rows = await res.json();
      if (!Array.isArray(rows) || rows.length === 0) {
        console.warn(`[precomputed] ${fileName}: empty or not an array — falling back`);
        return null;
      }
      memory.set(fileName, rows);
      // Not awaited: the study should draw as soon as the data is parsed, and persisting it is a
      // courtesy to the NEXT visit. A slow or full disk must not hold up this one.
      idbPut(fileName, rows);
      return rows;
    } catch (e) {
      console.warn(`[precomputed] ${fileName}: ${e.message} — falling back to the live study`);
      return null;
    } finally {
      inFlight.delete(fileName);
    }
  })();

  inFlight.set(fileName, request);
  return request;
}

/** The TrendPulse series, or null. */
export function loadTrendPulseDataset(opts) {
  return loadDataset(trendPulseFileName(), opts);
}

/**
 * The CTS timeline for an orb, RAW, or null when that orb has no file.
 *
 * <h2>The fold moved downstream, to `reversalBars`</h2>
 *
 * It used to happen here, on the parsed rows. The rule is now a FIVE-BAR cooldown and a bar window
 * cannot be computed from a calendar: this file is one row per day and the loader has no symbol,
 * no exchange and no session list. The same dataset serves SPY and an Indian equity with different
 * holidays, so "bar T+5" has no single meaning at the read.
 *
 * <p>So the rows arrive raw and `reversalBars` folds them the moment each date has been resolved
 * onto the chart's own axis — the one place the window is even definable.
 */
export function loadCtsDataset(orb, opts) {
  if (!hasPrecomputedOrb(orb)) return Promise.resolve(null);
  return loadDataset(ctsFileName(orb), opts);
}

/**
 * WHEN each marked day's configuration was tightest, as a Map of date to ISO instant.
 *
 * <h2>Why the chart needs this at all</h2>
 *
 * The reversals file says WHICH days are marked and nothing else. `/api/gann-confluence/classify`
 * recomputes the sky to describe them, and it samples ONE READING A DAY -- so for a day the
 * 30-minute scan found between two midnights it answers "does not qualify", the frontend drops it,
 * and the triangle is left with no index digit, no badge and no hover card. Measured on 2024 at
 * orb 1.0: 67 of 74 marked days, so 90% of that year's triangles had no description at all.
 *
 * <p>Handing these instants to classify costs nothing -- it is still one sample per date -- and it
 * makes the classifier agree with the data it is describing.
 *
 * <h2>⚠ A MISSING FILE IS NOT AN ERROR</h2>
 *
 * It degrades to exactly the previous behaviour: dates sampled at midnight, most of them
 * unclassified. The marks are the study and the description decorates them, so a failure here must
 * cost the decoration and never the finding. The bucket may also not have the file yet.
 */
export async function loadCtsDetail(orb, opts) {
  if (!hasPrecomputedOrb(orb)) return new Map();
  try {
    const rows = await loadDataset(ctsDetailFileName(orb), opts);
    const out = new Map();
    (Array.isArray(rows) ? rows : []).forEach((r) => {
      if (r && typeof r.date === 'string' && typeof r.peak === 'string') out.set(r.date, r.peak);
    });
    return out;
  } catch (e) {
    console.warn('[cts] peak instants unavailable; classifying at midnight, which describes '
      + 'only the days a daily sample can see:', e);
    return new Map();
  }
}

/** What is cached right now — for the status line and for tests. */
export function cachedDatasetNames() {
  return [...memory.keys()];
}
